import React, { ReactElement, useEffect, useState } from 'react'
import styled from 'styled-components'
import {
  Buttonlink,
  capitalize,
  Checkbox,
  ColorField,
  colors,
  Font,
  IOrganisationResponse as OrgResponse,
  itemWord,
  removeHash,
  SelectField,
} from '@one-tree/library'
import { get } from '../../helpers/APIHelper'
import useResponsive from '../../helpers/isResponsive'
import { Resource, IOrganisationPatchBody as OrgPatch } from '../../types/API'
import { Header, Section } from './WebShopHelper'

const Grid = styled.div`
  display: grid;
  grid-template-columns: 245px 245px;
  gap: 20px 25px;
  ${(props): string => props.theme.isSmallDesktop && 'grid-template-columns: 190px 190px'};
`
const Column = styled.div`
  display: flex;
  flex-direction: column;
  grid-row-gap: 20px;
`

type Props = {
  values: OrgResponse
  handleChange: <K extends keyof OrgPatch>(key: K, value: OrgPatch[K]) => void
}
function WebShopTheme({ values, handleChange }: Props): ReactElement {
  const [fonts, setFonts] = useState<Font[] | null>(null)

  useEffect(() => {
    (async (): Promise<void> => {
      setFonts((await get<Font[]>({ resource: Resource.Fonts })) || null)
    })()
  }, [])

  const isValidHexColor = (color: string): boolean => {
    const hexColorRegex = /^#?([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/
    return hexColorRegex.test(color)
  }

  const highlightValid = isValidHexColor(values.colourHighlight)
  const bodyTextValid = isValidHexColor(values.colourBodyText)
  const textOnHighlightValid = isValidHexColor(values.colourTextOnHighlight)

  const missingEssential = !highlightValid || !bodyTextValid || !textOnHighlightValid

  const addDefaults = (): void => {
    if (!highlightValid) handleChange('colourHighlight', removeHash(colors.logo))
    if (!bodyTextValid) handleChange('colourBodyText', removeHash(colors.darkestGray))
    if (!textOnHighlightValid) handleChange('colourTextOnHighlight', removeHash(colors.white))
  }

  const fontOptions = fonts
    ? fonts.map((font) => ({
        label: capitalize(font),
        value: font,
      }))
    : []

  const selectedFont = {
    label: capitalize(values.font),
    value: values.font,
  }

  const word = capitalize(itemWord(values.format))
  const placeholder = values.colourHighlight ? `#${values.colourHighlight}` : ''

  return (
    <Section>
      <Header>
        <h3>Theme</h3>
        <p>Change the general theme of your online web shop.</p>
        {missingEssential && (
          <p
            style={{
              color: `${colors.red}`,
              fontWeight: 'bold',
              maxWidth: '400px',
            }}
          >
            You are missing essential colours. Please fill in any red fields
            below, or{' '}
            <Buttonlink onClick={addDefaults}>add defaults</Buttonlink>.
          </p>
        )}
      </Header>
      <Grid theme={useResponsive()}>
        <Column>
          <SelectField
            options={fontOptions}
            label="Select a font"
            onChange={(option): void => {
              if (option) handleChange('font', option.value)
            }}
            value={selectedFont}
          />
          <ColorField
            label="Body text"
            onChange={(value): void => handleChange('colourBodyText', removeHash(value))}
            value={values.colourBodyText}
            isError={!bodyTextValid}
          />
          <ColorField
            label="Text on highlight"
            onChange={(value): void => handleChange('colourTextOnHighlight', removeHash(value))}
            value={values.colourTextOnHighlight}
            isError={!textOnHighlightValid}
          />
          <div>
            <ColorField
              label="Page background"
              onChange={(value): void => handleChange('colourPageBackground', removeHash(value))}
              value={values.colourPageBackground}
              disabled={values.transparentIframe}
            />
            <Checkbox
              label="Transparent background?"
              onChange={(value): void => handleChange('transparentIframe', value)}
              value={values.transparentIframe}
              style={{ marginTop: '7px' }}
            />
          </div>
        </Column>
        <Column>
          <ColorField
            label="Highlight"
            onChange={(value): void => handleChange('colourHighlight', removeHash(value))}
            value={values.colourHighlight}
            isError={!highlightValid}
          />
          <ColorField
            label={`${word} name`}
            onChange={(value): void => handleChange('colourHeaderText', removeHash(value))}
            value={values.colourHeaderText}
            placeholder={placeholder}
          />
          <ColorField
            label={`${word} price`}
            onChange={(value): void => handleChange('colourPriceText', removeHash(value))}
            value={values.colourPriceText}
            placeholder={placeholder}
          />
        </Column>
      </Grid>
    </Section>
  )
}
export default WebShopTheme
